import styled from 'styled-components'
import React, {useEffect, useState} from 'react'
import TextField from '@material-ui/core/TextField'
import {handleAutocomplete, onChangeStringHandler} from 'lib/dom'
import {Autocomplete} from '@material-ui/lab'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {ajax, useObserve} from 'lib/rx'
import {debounceTime, filter, map, switchMap, tap} from 'rxjs/operators'
import {useAuthToken} from 'obvio/auth'
import {Attendee} from 'Event/attendee'

const MIN_SEARCH_CHARS = 3

export default function AttendeeSearchField(props: {
  value: Attendee | null
  onChange: (attendee: Attendee | null) => void
  disabled?: boolean
}) {
  const {value, onChange, disabled} = props
  const [name, setName] = useState('')
  const {value$, onReady} = useObserve(name)
  const [options, setOptions] = React.useState<Attendee[]>([])
  const [loading, setLoading] = React.useState(false)

  const {
    event: {slug},
  } = useEvent()
  const token = useAuthToken()

  useEffect(() => {
    if (!onReady) {
      return
    }

    value$
      .pipe(
        filter((value) => {
          return value.length >= MIN_SEARCH_CHARS
        }),
        tap(() => {
          setLoading(true)
        }),
        debounceTime(500),
        switchMap((name) => {
          const url = api(
            `/events/${slug}/attendees/?search=${encodeURIComponent(name)}`,
          )

          return ajax.get(url, {
            accessToken: token,
          })
        }),
        map((res) => res.response),
        tap(() => {
          setLoading(false)
        }),
      )
      .subscribe({
        next: (options) => {
          // DynamoDB -> RDS sync can leave `null` attendees in the results,
          // same as AttendeesProvider.
          setOptions(
            options.data.filter((attendee: Attendee | null) => !!attendee),
          )
        },
      })

    onReady()
  }, [value$, token, slug, onReady])

  return (
    <Box>
      <Autocomplete
        disablePortal
        disabled={disabled}
        options={options}
        value={value || null}
        onChange={handleAutocomplete(onChange)}
        getOptionLabel={getAttendeeLabel}
        loading={loading}
        closeIcon=""
        aria-label="preview attendee search"
        noOptionsText={'Type to search. Minimum length: 3 characters.'}
        getOptionSelected={(option, value) => option.id === value.id}
        renderInput={(params) => {
          return (
            <>
              <TextField
                {...params}
                onChange={onChangeStringHandler(setName)}
                variant={'outlined'}
                disabled={disabled}
                fullWidth
                helperText={''}
                label={'Preview as attendee'}
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'preview attendee id',
                }}
                InputProps={{
                  ...params.InputProps,
                }}
              />
            </>
          )
        }}
      />
    </Box>
  )
}

function getAttendeeLabel(attendee: Attendee) {
  const name = [attendee.first_name, attendee.last_name]
    .filter(Boolean)
    .join(' ')

  if (!name) {
    return attendee.email
  }

  return `${name} - ${attendee.email}`
}

const Box = styled.div`
  position: relative;
  .MuiAutocomplete-popper {
    margin-top: -20px !important;
  }
`
